import "~/misc/common";
import { p } from "~/misc/common";
import { Controller } from "@hotwired/stimulus";
import moment from "moment";

interface Image {
  src: string;
  low: string;
  ts: number;
}

export default class extends Controller {
  connect() {
    p('initPhotoswipeWebcam');

    var images: Image[] = [];
    let currentIndex = 0;
    let scale = 1;
    let posX = 0, posY = 0;
    let isDragging = false, startX = 0, startY = 0;

    // pinch helpers
    let pinchStartDist: number | null = null;
    let pinchStartScale = 1;
    let pinchOffsetX = 0;
    let pinchOffsetY = 0;
    let pinchStartPosX = 0;
    let pinchStartPosY = 0;

    const MIN_SCALE = 1.0;
    const MAX_SCALE = 10.0;
    const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

    function updateImage(useLow = false) {
      $("#viewer-image").attr("src", useLow ? images[currentIndex].low : images[currentIndex].src);
      $(".slider-clock").text(moment.unix(images[currentIndex].ts).format("HH:mm:ss"));
      $("#viewer-slider").val(currentIndex);
      applyTransform();
    }

    function applyTransform() {
      $("#viewer-image").css(
        "transform",
        `translate(${posX}px, ${posY}px) scale(${scale})`
      );
    }

    function resetTransform() {
      scale = 1;
      posX = 0;
      posY = 0;
      applyTransform();
    }

    // open viewer
    $(".webcam-preview").on("click", function () {
      images = $(this).data('list');
      if (!images || !images.length) return;
      $("#viewer-slider").attr("max", images.length - 1);
      currentIndex = images.length - 1;
      resetTransform();
      updateImage();
      $("#viewer-overlay").fadeIn().focus();

      // request fullscreen
      const overlay = document.getElementById("viewer-overlay") as any;
      if (overlay.requestFullscreen) {
        overlay.requestFullscreen();
      } else if (overlay.webkitRequestFullscreen) { // Safari
        overlay.webkitRequestFullscreen();
      } else if (overlay.msRequestFullscreen) { // IE/Edge legacy
        overlay.msRequestFullscreen();
      }

      try { history.pushState({ viewer: true }, ""); } catch (e) { }
    });

    // close viewer
    function closeViewer() {
      $("#viewer-overlay").fadeOut();

      try { history.back(); } catch (e) { }
    }

    $(document).on("keydown", function (e) {
      if (!$("#viewer-overlay").is(":visible")) return;

      if (e.key === "Escape") {
        closeViewer();
      } else if (e.key === "ArrowLeft") {
        if (currentIndex > 0) {
          currentIndex--;
          updateImage();
        }
        e.preventDefault(); // prevent scrolling
      } else if (e.key === "ArrowRight") {
        if (currentIndex < images.length - 1) {
          currentIndex++;
          updateImage();
        }
        e.preventDefault(); // prevent scrolling
      }
    });

    window.addEventListener("popstate", function () {
      if ($("#viewer-overlay").is(":visible")) {
        $("#viewer-overlay").fadeOut();
      }
    });

    // detect fullscreen exit
    document.addEventListener("fullscreenchange", function () {
      if (!document.fullscreenElement && $("#viewer-overlay").is(":visible")) {
        // fullscreen was exited (Escape pressed)
        $("#viewer-overlay").fadeOut();
      }
    });

    document.addEventListener("webkitfullscreenchange", function () {
      if (!(document as any).webkitFullscreenElement && $("#viewer-overlay").is(":visible")) {
        $("#viewer-overlay").fadeOut();
      }
    });

    // live scrub → low res
    $("#viewer-slider").on("input", function () {
      currentIndex = +$(this).val()!;
      updateImage(true);
    });

    // release → high res
    $("#viewer-slider").on("change", function () {
      currentIndex = +$(this).val()!;
      updateImage(); // high res
    });

    // click navigation
    $("#viewer-overlay").on("click", function (e) {
      if ($(e.target).is("#viewer-slider, #viewer-image")) return;
      const width = $(this).width() || 0;
      if (e.pageX < width / 2 && currentIndex > 0) {
        currentIndex--;
      } else if (e.pageX >= width / 2 && currentIndex < images.length - 1) {
        currentIndex++;
      }
      updateImage();
    });

    /* ----------------
       WHEEL ZOOM (native listener, passive:false) — zooms under pointer
       ---------------- */
    const overlayEl = $("#viewer-overlay")[0];
    if (overlayEl) {
      overlayEl.addEventListener('wheel', function (e) {
        const targetEl = e.target as Element | null;
        if (targetEl && $(targetEl).closest(".controls").length) return;
        e.preventDefault();

        const imgEl = $("#viewer-image")[0];
        if (!imgEl) return;

        const oldScale = scale;
        const delta = e.deltaY;
        const factor = Math.exp(-delta * 0.001);
        const newScale = clamp(oldScale * factor, MIN_SCALE, MAX_SCALE);

        if (Math.abs(newScale - oldScale) < 1e-6) return;

        // --- compute local coords relative to current transform ---
        const rect = imgEl.getBoundingClientRect();
        const imgCenterX = rect.left + rect.width / 2;
        const imgCenterY = rect.top + rect.height / 2;

        // pointer offset from current center (in screen px)
        const pointerOffsetX = e.clientX - imgCenterX;
        const pointerOffsetY = e.clientY - imgCenterY;

        // convert to image-local coords
        const localX = pointerOffsetX / oldScale;
        const localY = pointerOffsetY / oldScale;

        // adjust posX/Y so that this local point stays fixed
        const ratio = newScale / oldScale;
        posX -= localX * (ratio - 1) * oldScale;
        posY -= localY * (ratio - 1) * oldScale;

        scale = newScale;
        applyTransform();
      }, { passive: false });
    }

    /* ----------------
       PINCH ZOOM (use pinchStartScale + correct math)
       ---------------- */
    // touchstart
    $("#viewer-overlay").on("touchstart", function (e) {
      const t = e.originalEvent!.touches;
      if (t.length === 2) {
        pinchStartDist = Math.hypot(t[0].pageX - t[1].pageX, t[0].pageY - t[1].pageY);
        pinchStartScale = scale;

        // midpoint between fingers in viewport
        const pinchMidX = (t[0].pageX + t[1].pageX) / 2;
        const pinchMidY = (t[0].pageY + t[1].pageY) / 2;

        const imgEl = $("#viewer-image")[0];
        const rect = imgEl.getBoundingClientRect();
        const rectCenterX = rect.left + rect.width / 2;
        const rectCenterY = rect.top + rect.height / 2;

        // store initial pointer offset relative to image center
        pinchOffsetX = pinchMidX - rectCenterX;
        pinchOffsetY = pinchMidY - rectCenterY;

        pinchStartPosX = posX;
        pinchStartPosY = posY;

      } else if (t.length === 1) {
        isDragging = true;
        startX = t[0].pageX - posX;
        startY = t[0].pageY - posY;
      }
    });

    // touchmove
    $("#viewer-overlay").on("touchmove", function (e) {
      const t = e.originalEvent!.touches;
      if (!t) return;

      if (t.length === 2 && pinchStartDist) {
        e.preventDefault();

        const newDist = Math.hypot(t[0].pageX - t[1].pageX, t[0].pageY - t[1].pageY);
        let newScale = clamp(pinchStartScale * (newDist / pinchStartDist), MIN_SCALE, MAX_SCALE);

        // adjust pan so midpoint stays under fingers
        const scaleFactor = newScale / pinchStartScale;
        posX = pinchStartPosX + pinchOffsetX * (1 - scaleFactor);
        posY = pinchStartPosY + pinchOffsetY * (1 - scaleFactor);

        scale = newScale;
        applyTransform();
        //clampPan(); // make sure image doesn't go offscreen
      } else if (isDragging && t.length === 1) {
        posX = t[0].pageX - startX;
        posY = t[0].pageY - startY;
        //clampPan();
        applyTransform();
      }
    });

    // touchend
    $("#viewer-overlay").on("touchend touchcancel", function (e) {
      const t = (e.originalEvent! as TouchEvent).touches;
      if (!t || t.length < 2) pinchStartDist = null;
      if (!t || t.length === 0) isDragging = false;
    });

    // clamp function for panning
    //function clampPan() {
    //  const imgEl = $("#viewer-image")[0];
    //  const rect = imgEl.getBoundingClientRect();
    //  const overlayRect = $("#viewer-overlay")[0].getBoundingClientRect();
    //
    //  // max offsets allowed
    //  const maxX = Math.max(0, (rect.width * scale - overlayRect.width) / 2);
    //  const maxY = Math.max(0, (rect.height * scale - overlayRect.height) / 2);
    //
    //  posX = clamp(posX, -maxX, maxX);
    //  posY = clamp(posY, -maxY, maxY);
    //}

    // drag for desktop (unchanged)
    $("#viewer-overlay").on("mousedown", function (e) {
      if (e.which === 1) {
        isDragging = true;
        startX = e.pageX - posX;
        startY = e.pageY - posY;
      }
    });

    $(document).on("mousemove", function (e) {
      if (isDragging) {
        posX = e.pageX - startX;
        posY = e.pageY - startY;
        applyTransform();
      }
    }).on("mouseup", function () {
      isDragging = false;
    });

    // stop drag/pan from slider or wrapper area
    $(".slider-wrapper, #viewer-slider").on("mousedown touchstart touchmove", function (e) {
      e.stopPropagation();
    });
  }
}
